// DS EDIT 5 — thread RAG scope control for the chat header (Layer 2 surface)
// and Chat Settings panel. Works with or without a threadId (pending scope on New Chat).
import { useEffect, useState, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { ragScopeService } from "@/services/ragScopeService";
import {
  getPendingRagScope,
  setPendingRagScope,
  subscribePendingRagScope,
} from "@/services/pendingRagScopeStore";
import { normalizeRagScope, scopeBadgeLabel, RAG_SCOPE_MAX } from "@/lib/ragScopePure";

const ICON_BUTTON_CLASS =
  "relative flex-shrink-0 h-9 w-9 sm:h-8 sm:w-8 after:absolute after:-inset-1 after:content-[''] sm:after:hidden";

type RagScopeControlProps = {
  /** When null/undefined, selection is held pending until the chat thread is created. */
  threadId?: string | null;
  /** header = Layers icon/badge + popover; panel = inline checkboxes in Chat Settings */
  variant?: "header" | "panel";
  /** When true (panel only), omit the Workspaces title block — accordion trigger supplies it. */
  hideHeading?: boolean;
};

function usePendingRagScopeSnapshot() {
  return useSyncExternalStore(subscribePendingRagScope, getPendingRagScope, () => null);
}

function useRagScopeEditor(threadId: string | null | undefined, active: boolean) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspaceId } = useWorkspace();
  const pending = usePendingRagScopeSnapshot();
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const scopeQuery = useQuery({
    queryKey: ["rag-scope", threadId],
    queryFn: async () => ragScopeService.getThreadScope(threadId!),
    enabled: !!threadId,
  });
  const workspacesQuery = useQuery({
    queryKey: ["accessible-workspaces"],
    queryFn: async () => ragScopeService.getAccessibleWorkspaces(),
    enabled: active,
  });

  const persistedScope = scopeQuery.data ?? null;
  const effectiveScope = threadId ? persistedScope : pending;

  useEffect(() => {
    if (!active) return;
    if (threadId) {
      setSelected(persistedScope ?? (currentWorkspaceId ? [currentWorkspaceId] : []));
    } else {
      setSelected(pending ?? (currentWorkspaceId ? [currentWorkspaceId] : []));
    }
  }, [active, threadId, persistedScope, pending, currentWorkspaceId]);

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) =>
      checked ? normalizeRagScope([...prev, id]) : prev.filter((x) => x !== id)
    );
  };

  const handleSave = async (onSaved?: () => void) => {
    setSaving(true);
    try {
      const extras = selected.filter((id) => id !== currentWorkspaceId);
      const next = extras.length > 0 ? selected : null;

      if (!threadId) {
        setPendingRagScope(next);
        toast({
          title: next ? "Workspace scope ready" : "Workspace scope cleared",
          description: next
            ? `${scopeBadgeLabel(next.length)} — will apply when you send the first message.`
            : "This chat will search its own workspace only.",
        });
        onSaved?.();
        return;
      }

      await ragScopeService.setThreadScope(threadId, next);
      queryClient.invalidateQueries({ queryKey: ["rag-scope", threadId] });
      toast({
        title: next ? "Knowledge scope updated" : "Knowledge scope cleared",
        description: next
          ? scopeBadgeLabel(next.length)
          : "This chat searches its own workspace only.",
      });
      onSaved?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      toast({
        title: "Couldn't update scope",
        description: msg.includes("workspace_not_authorized")
          ? "You are not a member of one of the selected workspaces."
          : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    currentWorkspaceId,
    selected,
    saving,
    scope: effectiveScope,
    workspacesQuery,
    workspaces: workspacesQuery.data ?? [],
    toggle,
    handleSave,
    isPendingMode: !threadId,
  };
}

function WorkspaceCheckboxList({
  currentWorkspaceId,
  workspaces,
  isLoading,
  selected,
  saving,
  toggle,
}: {
  currentWorkspaceId: string | null | undefined;
  workspaces: Array<{ id: string; name: string }>;
  isLoading: boolean;
  selected: string[];
  saving: boolean;
  toggle: (id: string, checked: boolean) => void;
}) {
  return (
    <div className="max-h-56 overflow-y-auto space-y-1.5">
      {!currentWorkspaceId && (
        <label className="flex items-center gap-2 text-sm opacity-70">
          <Checkbox checked disabled />
          <span>Personal (always searched)</span>
        </label>
      )}
      {isLoading && <p className="text-xs text-muted-foreground">Loading workspaces...</p>}
      {!isLoading && workspaces.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="text-rag-scope-no-workspaces">
          No team workspaces yet. Create or join a workspace (switcher above), then you can search
          across them here.
        </p>
      )}
      {workspaces.map((w) => {
        const isCurrent = w.id === currentWorkspaceId;
        return (
          <label
            key={w.id}
            className={`flex items-center gap-2 text-sm ${isCurrent ? "opacity-70" : ""}`}
            data-testid={`row-rag-scope-${w.id}`}
          >
            <Checkbox
              checked={isCurrent || selected.includes(w.id)}
              disabled={isCurrent || saving}
              onCheckedChange={(c) => toggle(w.id, c === true)}
            />
            <span className="truncate">
              {w.name}
              {isCurrent && <span className="text-muted-foreground"> (current)</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function RagScopeControl({
  threadId = null,
  variant = "header",
  hideHeading = false,
}: RagScopeControlProps) {
  const [open, setOpen] = useState(variant === "panel");
  const editor = useRagScopeEditor(threadId, variant === "panel" ? true : open);

  if (variant === "panel") {
    return (
      <div className="space-y-3" data-testid="panel-rag-scope">
        {!hideHeading && (
          <div>
            <p className="text-xs font-medium flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              Workspaces
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Choose which workspaces this chat searches. Up to {RAG_SCOPE_MAX}.
              {editor.isPendingMode
                ? " Selection applies when you send the first message."
                : ""}
            </p>
            {editor.scope && editor.scope.length > 0 && (
              <Badge
                variant="outline"
                className="mt-1.5 border-teal-500/40 bg-teal-500/10 text-teal-700 dark:text-teal-300"
                data-testid="badge-rag-scope-panel"
              >
                <Layers className="h-3 w-3 mr-1" />
                {scopeBadgeLabel(editor.scope.length)}
              </Badge>
            )}
          </div>
        )}

        {hideHeading && (
          <p className="text-xs text-muted-foreground">
            Choose which workspaces this chat searches. Up to {RAG_SCOPE_MAX}.
            {editor.isPendingMode
              ? " Selection applies when you send the first message."
              : ""}
          </p>
        )}

        <WorkspaceCheckboxList
          currentWorkspaceId={editor.currentWorkspaceId}
          workspaces={editor.workspaces}
          isLoading={editor.workspacesQuery.isLoading}
          selected={editor.selected}
          saving={editor.saving}
          toggle={editor.toggle}
        />

        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => void editor.handleSave()}
            disabled={editor.saving}
            data-testid="button-save-rag-scope"
          >
            {editor.saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            {editor.isPendingMode ? "Save for this chat" : "Save"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {editor.scope && editor.scope.length > 0 ? (
          <Badge
            variant="outline"
            role="button"
            tabIndex={0}
            className="cursor-pointer border-teal-500/40 bg-teal-500/10 text-teal-700 dark:text-teal-300 hover-elevate"
            title="Choose which workspaces this chat searches"
            data-testid="badge-rag-scope"
          >
            <Layers className="h-3 w-3 mr-1" />
            {scopeBadgeLabel(editor.scope.length)}
          </Badge>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={ICON_BUTTON_CLASS}
            title="Knowledge scope — choose which workspaces this chat searches"
            data-testid="button-rag-scope"
          >
            <Layers className="h-4 w-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">Knowledge scope</p>
            <p className="text-xs text-muted-foreground">
              Choose which workspaces this chat searches. Up to {RAG_SCOPE_MAX}.
              {editor.isPendingMode
                ? " Applies when you send the first message."
                : ""}
            </p>
          </div>

          <WorkspaceCheckboxList
            currentWorkspaceId={editor.currentWorkspaceId}
            workspaces={editor.workspaces}
            isLoading={editor.workspacesQuery.isLoading}
            selected={editor.selected}
            saving={editor.saving}
            toggle={editor.toggle}
          />

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={editor.saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void editor.handleSave(() => setOpen(false))}
              disabled={editor.saving}
              data-testid="button-save-rag-scope"
            >
              {editor.saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
